import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  serializeConversationPayload,
  serializePresignUrlPyalod,
} from "../utils/serializers";
import { conversationsAPI } from "../api";
import { handleErrors, type ApiError } from "../../../api/errorUtils";
import type {
  GetConversationsResponse,
  GetConversationsRequest,
  GetUnreadConversationsResponse,
  GetConversationMessagesResponse,
  GetConversationMessagesRequest,
  ReadConversationMessagesRequest,
  ResendConversationMessagesRequest,
  ConversationMessageDetail,
  DeleteConversationMessageRequest,
  DeleteConversationMessageFileRequest,
  UpdateConversationMessageRequest,
  CreateConversationRequest,
  ConversationDetail,
  ConversationMessagesRequest,
  PresignUploadResponse,
  PresignUrlRequest,
  PresignViewResponse,
} from "../ts";

export const GetConversationsThunk = createAsyncThunk<
  GetConversationsResponse,
  GetConversationsRequest
>("conversations/get_conversations", async (requestData, thunkAPI) => {
  try {
    const response = await conversationsAPI.getConversationsList(requestData);
    const { data } = response;
    return data;
  } catch (err: unknown) {
    return handleErrors(err as ApiError, thunkAPI);
  }
});

export const GetConversationsUnreadThunk =
  createAsyncThunk<GetUnreadConversationsResponse>(
    "conversations/get_unread_count",
    async (_, thunkAPI) => {
      try {
        const response = await conversationsAPI.getUnreadMessagesCount();
        const { data } = response;
        return data;
      } catch (err: unknown) {
        return handleErrors(err as ApiError, thunkAPI);
      }
    },
  );

export const GetConversationMessagesThunk = createAsyncThunk<
  GetConversationMessagesResponse,
  GetConversationMessagesRequest
>("conversations/get_messages", async (requestData, thunkAPI) => {
  try {
    const response =
      await conversationsAPI.getConversationMessages(requestData);
    const { data } = response;
    return data;
  } catch (err: unknown) {
    return handleErrors(err as ApiError, thunkAPI);
  }
});

export const ReadConversationMessagesThunk = createAsyncThunk<
  null,
  ReadConversationMessagesRequest
>("conversations/read_messages", async (requestData, thunkAPI) => {
  try {
    const response =
      await conversationsAPI.readConversationMessages(requestData);
    const { data } = response;
    return data;
  } catch (err: unknown) {
    return handleErrors(err as ApiError, thunkAPI);
  }
});

export const ResendConversationMessagesThunk = createAsyncThunk<
  null,
  ResendConversationMessagesRequest
>("conversations/resend_messages", async (requestData, thunkAPI) => {
  try {
    const response =
      await conversationsAPI.resendConversationMessages(requestData);
    const { data } = response;
    return data;
  } catch (err: unknown) {
    return handleErrors(err as ApiError, thunkAPI);
  }
});

export const CreateConversationMessagesThunk = createAsyncThunk<
  ConversationMessageDetail[],
  ReadConversationMessagesRequest
>("conversations/create_messages", async (requestData, thunkAPI) => {
  try {
    const serializedFormData = await serializeConversationPayload(
      requestData as ConversationMessagesRequest,
    );
    const response = await conversationsAPI.createConversationMessages(
      requestData.conversation_id,
      serializedFormData,
    );
    const { data } = response;
    return data;
  } catch (err: unknown) {
    return handleErrors(err as ApiError, thunkAPI);
  }
});

export const DeleteConversationMessagesThunk = createAsyncThunk<
  ConversationMessageDetail[],
  DeleteConversationMessageRequest
>("conversations/delete_message", async (requestData, thunkAPI) => {
  try {
    const response =
      await conversationsAPI.deleteConversationMessages(requestData);
    const { data } = response;
    return data;
  } catch (err: unknown) {
    return handleErrors(err as ApiError, thunkAPI);
  }
});

export const DeleteConversationMessageFileThunk = createAsyncThunk<
  ConversationMessageDetail,
  DeleteConversationMessageFileRequest
>("conversations/delete_message_file", async (requestData, thunkAPI) => {
  try {
    const response = await conversationsAPI.deleteConversationMessageFile(
      requestData.conversation_id,
      requestData,
    );
    const { data } = response;
    return data;
  } catch (err: unknown) {
    return handleErrors(err as ApiError, thunkAPI);
  }
});

export const UpdateConversationMessagesThunk = createAsyncThunk<
  ConversationMessageDetail[],
  UpdateConversationMessageRequest
>("conversations/update_message", async (requestData, thunkAPI) => {
  try {
    const serializedFormData = await serializeConversationPayload(requestData);
    const response = await conversationsAPI.updateConversationMessages(
      requestData.conversation_id,
      serializedFormData,
    );
    const { data } = response;
    return data;
  } catch (err: unknown) {
    return handleErrors(err as ApiError, thunkAPI);
  }
});

export const CreateConversationThunk = createAsyncThunk<
  { conversation_id: string },
  CreateConversationRequest
>("conversations/create_conversation", async (requestData, thunkAPI) => {
  try {
    const response = await conversationsAPI.createConversation(requestData);
    const { data } = response;
    return data;
  } catch (err: unknown) {
    return handleErrors(err as ApiError, thunkAPI);
  }
});

export const HandleUndeliveredMessagesThunk = createAsyncThunk<
  ConversationMessageDetail[],
  ReadConversationMessagesRequest
>(
  "conversations/handle_undelivered_messages",
  async (requestData, thunkAPI) => {
    try {
      const serializedFormData = await serializeConversationPayload(
        requestData as ConversationMessagesRequest,
      );
      const response = await conversationsAPI.createConversationMessages(
        requestData.conversation_id,
        serializedFormData,
      );
      const { data } = response;
      return data;
    } catch (err: unknown) {
      return handleErrors(err as ApiError, thunkAPI);
    }
  },
);

export const GetConversationThunk = createAsyncThunk<
  ConversationDetail,
  { companionId: string }
>("conversations/get_conversation", async (requestData, thunkAPI) => {
  try {
    const response = await conversationsAPI.getConversation(
      requestData.companionId,
    );
    const { data } = response;
    return data;
  } catch (err: unknown) {
    return handleErrors(err as ApiError, thunkAPI);
  }
});

export const CreatePresignUploadThunk = createAsyncThunk<
  PresignUploadResponse, 
  PresignUrlRequest
>("conversations/create_presign_upload", async (requestData, thunkAPI) => {
  try {
    const serializedFormData = await serializePresignUrlPyalod(requestData);
    const response = await conversationsAPI.createPresignUrl(
      requestData.conversation_id,
      serializedFormData,
    );

    return response.data;
  } catch (err: unknown) {
    return handleErrors(err as ApiError, thunkAPI);
  }
});

export const GetPresignUrlThunk = createAsyncThunk<
  PresignViewResponse,
  PresignUrlRequest
>("conversations/get_presign_view", async (requestData, thunkAPI) => {
  try {
    const response = await conversationsAPI.getPresignUrl(requestData);

    return response.data;
  } catch (err: unknown) {
    return handleErrors(err as ApiError, thunkAPI);
  }
});

const conversationsThunk = {
  GetConversationsThunk,
  GetConversationMessagesThunk,
  ReadConversationMessagesThunk,
  ResendConversationMessagesThunk,
  CreateConversationMessagesThunk,
  GetConversationsUnreadThunk,
  CreateConversationThunk,
  HandleUndeliveredMessagesThunk,
  GetConversationThunk,
  DeleteConversationMessagesThunk,
  UpdateConversationMessagesThunk,
  DeleteConversationMessageFileThunk,
  CreatePresignUploadThunk,
  GetPresignUrlThunk
};

export default conversationsThunk;
